import { useEffect, useMemo, useRef } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useContentToken,
  useCollectionBinding,
  useSlotGroup,
  useGallery,
  EditableToken,
  EditableSlotGroup,
  RichTextContent,
} from '@vibesites/runtime/react';
import type { PhotoGallery } from '@vibesites/schema';
import { VibesitesImage } from '@vibesites/ui-primitives';

/**
 * Weidner override of the Aster FloorplansList. Identical to the parent theme's
 * component except the full-section image overlay is painted in solid black at
 * 50% opacity instead of `--vibe-color-primary` at 77%, matching the black
 * scrim now used across every page hero so the property photo reads through
 * more clearly under a neutral tint.
 */

// ============================================================================
// Types & helpers
// ============================================================================

interface Floorplan {
  floorplan_id?: string | null;
  floorplan_name?: string | null;
  floorplan_image_url?: string | null;
  floorplan_beds_min?: string | null;
  floorplan_beds_max?: string | null;
  floorplan_baths_min?: string | null;
  floorplan_sqft_min?: string | null;
  floorplan_sqft_max?: string | null;
  floorplan_price_min?: string | null;
  floorplan_price_max?: string | null;
  floorplan_available_units?: string | null;
}

/** A bedroom category derived from the floorplans collection. */
interface BedroomCategory {
  key: string;
  label: string;
  param: string;
}

const BED_ORDER = ['studio', 'one', 'two', 'three', 'four', 'five'];

/** Map a raw bedroom count ("0", "2", "studio", …) to a category key. */
function bedCountToKey(bedsRaw: string | null | undefined): string {
  if (!bedsRaw) return 'studio';
  const trimmed = bedsRaw.trim().toLowerCase();
  if (trimmed === '0' || trimmed === 'studio' || trimmed === 'std') return 'studio';
  const num = parseInt(trimmed, 10);
  if (isNaN(num) || num <= 0) return 'studio';
  return BED_ORDER[num] || `${num}`;
}

/** Human-readable label for a category key. */
function keyToLabel(key: string): string {
  const labels: Record<string, string> = {
    studio: 'Studio',
    one: '1 Bedroom',
    two: '2 Bedroom',
    three: '3 Bedroom',
    four: '4 Bedroom',
    five: '5 Bedroom',
  };
  return labels[key] || `${key} Bedroom`;
}

/** Group the floorplans collection into ordered bedroom categories. */
function groupFloorplansByBeds(floorplans: Floorplan[]): BedroomCategory[] {
  const keys = new Set<string>();
  for (const plan of floorplans) {
    keys.add(bedCountToKey(plan.floorplan_beds_min));
  }

  return Array.from(keys)
    .sort((a, b) => {
      const ai = BED_ORDER.indexOf(a);
      const bi = BED_ORDER.indexOf(b);
      return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
    })
    .map((key) => ({ key, label: keyToLabel(key), param: key }));
}

// Shown on the company theme (and any property without floor plans) so the
// section always renders the four representative unit types.
const FALLBACK_CATEGORIES: BedroomCategory[] = [
  { key: 'studio', label: 'Studio', param: 'studio' },
  { key: 'one', label: '1 Bedroom', param: 'one' },
  { key: 'two', label: '2 Bedroom', param: 'two' },
  { key: 'three', label: '3 Bedroom', param: 'three' },
];

/** Derive the circular badge label (S / 1 / 2 / …) from a category key. */
function badgeForKey(key: string, index: number): string {
  if (key === 'studio') return 'S';
  const ordered = BED_ORDER.indexOf(key);
  if (ordered > 0) return String(ordered);
  const numeric = key.match(/\d+/);
  if (numeric) return numeric[0];
  return String(index + 1);
}

/** Abstract line-art floor plan glyph, open at the top-right for the badge. */
function FloorPlanIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 120 108"
      fill="none"
      stroke="currentColor"
      strokeWidth="6"
      strokeLinecap="square"
      strokeLinejoin="miter"
      className={className}
      aria-hidden="true"
    >
      {/* Outer walls — open at the top-right corner where the badge sits */}
      <polyline points="80,6 6,6 6,102 114,102 114,52" />
      {/* Top-left room */}
      <polyline points="6,42 46,42 46,6" />
      {/* Central dividing walls */}
      <polyline points="46,42 46,70 84,70 84,102" />
      {/* Right room wall */}
      <line x1="114" y1="70" x2="84" y2="70" />
    </svg>
  );
}

// ============================================================================
// Smart interior-photo selection — prefer a genuine interior shot of the unit
// ============================================================================

const INTERIOR_KEYWORDS = [
  'interior', 'apartment', 'unit', 'living', 'kitchen', 'bedroom', 'bathroom',
  'home', 'modern', 'luxury', 'inside', 'hallway', 'dining', 'closet',
];

// Strong signals that the unit is staged/lived-in rather than bare — these
// weigh more heavily so a furnished shot beats a plain "living room" one.
const FURNISHED_KEYWORDS = ['furnished', 'staged', 'model', 'sofa', 'couch', 'bed ', 'decor', 'furniture'];

// Photos whose text matches these are NOT usable feature images: floor-plan
// diagrams / maps, or bare/vacant units we must never surface.
const EXCLUDE_KEYWORDS = [
  // Diagrams & maps
  'floor plan', 'floorplan', 'site map', 'sitemap', 'diagram', 'blueprint',
  // Empty / unfurnished units
  'empty', 'vacant', 'unfurnished', 'no furniture', 'move-in ready', 'move in ready',
];

function galleryText(photo: PhotoGallery): string {
  return [photo.title, photo.description, photo.alt_text]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
}

function isExcludedImage(photo: PhotoGallery): boolean {
  const text = galleryText(photo);
  return EXCLUDE_KEYWORDS.some((kw) => text.includes(kw));
}

function scoreInterior(photo: PhotoGallery): number {
  const text = galleryText(photo);
  if (!text) return 0;
  const interior = INTERIOR_KEYWORDS.reduce((s, kw) => s + (text.includes(kw) ? 1 : 0), 0);
  // Furnished cues count double so a genuinely staged interior wins.
  const furnished = FURNISHED_KEYWORDS.reduce((s, kw) => s + (text.includes(kw) ? 2 : 0), 0);
  return interior + furnished;
}

/**
 * Index of the best interior photo in the gallery, skipping floor-plan
 * diagrams AND empty / unfurnished units.
 */
function findInteriorPhotoIndex(gallery: PhotoGallery[]): number {
  if (!gallery.length) return 0;

  let bestIndex = -1;
  let bestScore = 0;
  for (let i = 0; i < gallery.length; i++) {
    if (isExcludedImage(gallery[i])) continue;
    const score = scoreInterior(gallery[i]);
    if (score > bestScore) {
      bestScore = score;
      bestIndex = i;
    }
  }
  if (bestIndex >= 0) return bestIndex;

  // No keyword match — pick the first non-excluded photo, preferring a
  // non-hero shot when the gallery has more than one usable image.
  const usable: number[] = [];
  for (let i = 0; i < gallery.length; i++) {
    if (!isExcludedImage(gallery[i])) usable.push(i);
  }
  if (!usable.length) return 0;
  return usable.find((i) => i === 1) ?? usable[0];
}

// ============================================================================
// Static slot group definitions (for contentSchema — must be inline literals)
// ============================================================================

const featureImageDef: SlotGroupDefinition = {
  label: 'Feature Image',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[1].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[1].alt_text' },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 1,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
  generator: {
    prompt:
      'Pick a photo of a furnished apartment interior — a real living space with visible furniture and decor. Avoid empty or unfurnished units, and avoid floor-plan diagrams, exterior shots, and amenity/common-area photos.',
    context:
      'Feature image beside the floor-plan copy in the Floor Plans section; it should show what a lived-in home looks like.',
    sourceCollection: 'photo_gallery',
  },
};

/** Build a feature-image slot def pointed at a specific gallery index. */
function makeFeatureImageDef(index: number): SlotGroupDefinition {
  return {
    label: 'Feature Image',
    fields: {
      image: { label: 'Image', type: 'image', defaultField: `photo_gallery[${index}].image` },
      alt: { label: 'Alt Text', type: 'text', defaultField: `photo_gallery[${index}].alt_text` },
    },
    sourceBinding: {
      collection: 'photo_gallery',
      defaultIndex: index,
      fieldMappings: { image: 'image', alt: 'alt_text' },
    },
  };
}

const featureCtaDef: SlotGroupDefinition = {
  label: 'Feature CTA',
  fields: {
    url: { label: 'Button URL', type: 'url', defaultValue: '/floorplans' },
    label: { label: 'Button Label', type: 'text', defaultValue: 'Explore Now' },
    _target: { label: 'Link Target', type: 'text', defaultValue: '_self' },
  },
};

// ============================================================================
// Scroll reveal — slide elements up into view as they enter the viewport
// ============================================================================

/**
 * Observes descendants marked with `data-reveal` and toggles the `is-visible`
 * class as each scrolls into view (one-shot). Falls back to showing everything
 * immediately when IntersectionObserver is unavailable (e.g. during SSG) or the
 * user prefers reduced motion.
 */
function useScrollReveal<T extends HTMLElement>() {
  const ref = useRef<T>(null);

  useEffect(() => {
    const root = ref.current;
    if (!root) return;

    const els = Array.from(root.querySelectorAll<HTMLElement>('[data-reveal]'));
    if (!els.length) return;

    const reduceMotion =
      typeof window !== 'undefined' &&
      window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

    if (reduceMotion || typeof IntersectionObserver === 'undefined') {
      els.forEach((el) => el.classList.add('is-visible'));
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        }
      },
      { threshold: 0.15, rootMargin: '0px 0px -10% 0px' },
    );

    els.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return ref;
}

// ============================================================================
// Component
// ============================================================================

export function FloorplansList() {
  const revealRef = useScrollReveal<HTMLDivElement>();
  const image = useContentToken('floorplanslist.section_image');
  const gallery = useGallery();
  const interiorIndex = useMemo(() => findInteriorPhotoIndex(gallery), [gallery]);
  const featureImageDynamicDef = useMemo(() => makeFeatureImageDef(interiorIndex), [interiorIndex]);
  const featureImage = useSlotGroup('floorplanslist.feature_image', featureImageDynamicDef);
  const featureCta = useSlotGroup('floorplanslist.feature_cta', featureCtaDef);

  // Derive bedroom categories from the floorplans collection (grouped by bed
  // count), falling back to representative unit types when none are present.
  const floorplans = useCollectionBinding<Floorplan>('floorplanslist.items', {
    collection: 'floorplans',
  });
  const categories = useMemo(() => {
    const grouped = floorplans?.length ? groupFloorplansByBeds(floorplans) : [];
    return (grouped.length > 0 ? grouped : FALLBACK_CATEGORIES).slice(0, 4);
  }, [floorplans]);

  return (
    <section className="relative overflow-hidden bg-[var(--vibe-color-background)] min-h-[720px] md:min-h-[1100px]">
      {/* Full-section property image, clipped to an angled top edge */}
      <div className="fp-section-image absolute inset-0">
        <EditableToken
          path="floorplanslist.section_image"
          type="image"
          as="div"
          label="Section Image"
          className="w-full h-full"
        >
          {image ? (
            <VibesitesImage
              url={image}
              alt="Property"
              sizes="100vw"
              className="absolute inset-0 w-full h-full object-cover object-center"
            />
          ) : (
            <div className="absolute inset-0 bg-[var(--vibe-color-surface)]" />
          )}
        </EditableToken>
        {/* Black overlay at 50% opacity — Weidner override of the theme
            primary-color scrim, matching the page heroes so more of the
            property photo reads through. */}
        <div className="absolute inset-0 bg-black opacity-50" />
      </div>

      {/* Floor plan type CTAs + feature block overlaid on top of the image section.
          justify-end bottom-anchors both groups so the feature block's interior image
          keeps overhanging the angled edge, while the gap value sets the (smaller)
          distance between the icons and the feature block. Section height / clip are
          unchanged so the background image keeps its size and angles. */}
      <div
        ref={revealRef}
        className="relative z-10 flex min-h-[720px] md:min-h-[1100px] flex-col items-center justify-end gap-12 px-6 pt-44 pb-12 md:gap-16 md:pt-24 md:pb-20"
      >
        {/* Flex + justify-center so 1–4 bedroom types stay grouped in the
            middle. A fixed 4-column grid left-aligns leftover empty cells
            when a property only has two or three unit types. */}
        <div className="flex w-full max-w-6xl flex-wrap justify-center gap-x-8 gap-y-14 md:gap-x-16 md:gap-y-12">
          {categories.map((category, index) => {
            const name = category.label;
            const badge = badgeForKey(category.key, index);
            const url = `/floor-plans?bedroom=${category.param}`;

            return (
              <a
                key={category.key}
                href={url}
                data-reveal
                style={{ transitionDelay: `${index * 100}ms` }}
                className="fp-reveal flex w-36 flex-col items-center text-center hover:scale-105 sm:w-44 md:w-52"
              >
                <div className="relative w-24 sm:w-32 md:w-44">
                  <FloorPlanIcon className="h-auto w-full text-white drop-shadow-md" />
                  <span
                    className="absolute -top-2 -right-1 flex h-9 w-9 items-center justify-center rounded-full bg-white font-heading text-lg font-normal text-[var(--vibe-color-primary)] shadow-md sm:-top-3 sm:-right-2 sm:h-12 sm:w-12 sm:text-2xl md:h-16 md:w-16 md:text-4xl"
                  >
                    {badge}
                  </span>
                </div>
                <span
                  className="mt-5 whitespace-nowrap font-heading text-sm font-normal uppercase tracking-wide text-white drop-shadow-sm md:text-xl"
                >
                  {name}
                </span>
              </a>
            );
          })}
        </div>

        {/* Feature block: subtitle, paragraph, CTA + image. items-start top-aligns the
            text with the image so the taller image extends lower, past the angled edge. */}
        <div className="grid w-full max-w-6xl items-start gap-10 md:grid-cols-2 md:gap-16">
          {/* Text column */}
          <div className="flex flex-col items-start">
            <RichTextContent
              path="floorplanslist.feature_title"
              label="Feature Title"
              defaultValue="<p>Spacious Apartment Homes</p>"
              as="h2"
              className="fp-feature-title-font font-heading text-3xl font-normal text-white md:text-[2.75rem]"
            />

            <RichTextContent
              path="floorplanslist.feature_body"
              label="Feature Body Text"
              defaultValue="<p>Discover the allure of our diverse floor plans, ranging from chic studios to expansive three-bedroom residences. Each layout is thoughtfully crafted to provide a harmonious blend of functionality and sophistication.</p>"
              as="div"
              className="fp-feature-body-font mt-8 max-w-xl text-base leading-relaxed text-white/90 md:text-lg"
            />

            <EditableSlotGroup groupPath="floorplanslist.feature_cta" definition={featureCtaDef}>
              <a
                href={featureCta.url || '/floorplans'}
                target={featureCta._target || '_self'}
                className="mt-10 inline-flex items-center justify-center bg-[var(--vibe-color-secondary)] px-10 py-4 font-heading text-sm font-normal uppercase tracking-widest text-[var(--vibe-color-text-on-secondary)] shadow-md transition-colors hover:bg-white hover:text-[var(--vibe-color-secondary)]"
              >
                {featureCta.label || 'Explore Now'}
              </a>
            </EditableSlotGroup>
          </div>

          {/* Image column — slides in from the right as it enters the viewport */}
          <div
            data-reveal
            className="fp-reveal-right relative aspect-[3/2] w-full overflow-hidden shadow-lg"
          >
            <EditableSlotGroup groupPath="floorplanslist.feature_image" definition={featureImageDynamicDef}>
              {featureImage.image ? (
                <VibesitesImage
                  url={featureImage.image}
                  alt={featureImage.alt || 'Property interior'}
                  sizes="(min-width: 768px) 50vw, 100vw"
                  className="absolute inset-0 h-full w-full object-cover object-center"
                />
              ) : (
                <div className="absolute inset-0 bg-[var(--vibe-color-surface)]" />
              )}
            </EditableSlotGroup>
          </div>
        </div>
      </div>

      <style>{`
        /* Scroll reveal: elements start shifted down + transparent, then slide
           up into place once the observer adds .is-visible. */
        .fp-reveal {
          opacity: 0;
          transform: translateY(40px);
          transition: opacity 0.7s ease, transform 0.7s cubic-bezier(0.16, 1, 0.3, 1);
          will-change: opacity, transform;
        }
        .fp-reveal.is-visible {
          opacity: 1;
          transform: none;
        }
        @media (prefers-reduced-motion: reduce) {
          .fp-reveal {
            opacity: 1;
            transform: none;
            transition: none;
          }
        }
        /* Feature image reveal: starts shifted to the right + transparent, then
           slides left into place once the observer adds .is-visible. */
        .fp-reveal-right {
          opacity: 0;
          transform: translateX(60px);
          transition: opacity 0.7s ease, transform 0.7s cubic-bezier(0.16, 1, 0.3, 1);
          will-change: opacity, transform;
        }
        .fp-reveal-right.is-visible {
          opacity: 1;
          transform: none;
        }
        @media (prefers-reduced-motion: reduce) {
          .fp-reveal-right {
            opacity: 1;
            transform: none;
            transition: none;
          }
        }
        /* Feature title + body sit on the black 50% photo scrim. Saved
           editor HTML often carries a dark inline color; pin white so the
           copy stays readable the same way inner-page heroes do. */
        .fp-feature-title-font,
        .fp-feature-title-font * {
          color: #fff !important;
        }
        .fp-feature-body-font,
        .fp-feature-body-font * {
          font-family: var(--vibe-font-body);
          color: #fff !important;
        }
        /* Full-section image clipped to angled edges: the top edge slopes down
           from left to right, and the bottom edge slopes down from right to
           left (the two angles run in opposite directions). */
        .fp-section-image {
          clip-path: polygon(0 0, 100% 14%, 100% 86%, 0 100%);
          -webkit-clip-path: polygon(0 0, 100% 14%, 100% 86%, 0 100%);
        }
        @media (min-width: 768px) {
          .fp-section-image {
            clip-path: polygon(0 0, 100% 18%, 100% 82%, 0 100%);
            -webkit-clip-path: polygon(0 0, 100% 18%, 100% 82%, 0 100%);
          }
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'FloorplansList',
  displayName: 'Floor Plans List',
  tokens: {
    'floorplanslist.section_image': {
      label: 'Section Image',
      type: 'image',
      defaultField: 'property_featured_image',
    },
    'floorplanslist.feature_title': {
      label: 'Feature Title',
      type: 'richtext',
      defaultValue: '<p>Spacious Apartment Homes</p>',
    },
    'floorplanslist.feature_body': {
      label: 'Feature Body Text',
      type: 'richtext',
      defaultField: 'property_home_floorplans_blurb',
      defaultValue:
        '<p>Discover the allure of our diverse floor plans, ranging from chic studios to expansive three-bedroom residences. Each layout is thoughtfully crafted to provide a harmonious blend of functionality and sophistication.</p>',
    },
  },
  slotGroups: {
    'floorplanslist.feature_image': featureImageDef,
    'floorplanslist.feature_cta': featureCtaDef,
  },
  collectionBindings: {
    'floorplanslist.items': {
      label: 'Floor Plans',
      collection: 'floorplans',
    },
  },
};

(FloorplansList as any).contentSchema = contentSchema;

export default FloorplansList;
